/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import {
  AbstractResourceService,
  JsonApiListResourceMetadataBase,
  JsonApiResource,
  QueryParams
} from '@irian-ro/abstract-resource';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  OpportunityDTO,
  OpportunityListMeta,
  OpportunityListModel,
  OpportunityMetaDataModel,
  OpportunityMetaDetailModel,
  OpportunitySummaryVO,
  OpportunityVersionVoModel
} from '@well/domain';
import { EnvironmentService } from '@irian-ro/util-environment';

@Injectable({
  providedIn: 'root'
})
export class OpportunityResourceService extends AbstractResourceService<OpportunityListModel,
  OpportunityDTO,
  OpportunityListMeta,
  OpportunityMetaDataModel> {
  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('opportunityUrl'),
      OpportunityListModel,
      OpportunityDTO,
      OpportunityListMeta,
      OpportunityMetaDataModel
    );
  }

  getOpportunityTemplate(customerUuid: string): Observable<JsonApiResource<OpportunityDTO, OpportunityMetaDataModel>> {
    let path = '';
    if (customerUuid) {
      path = '/' + customerUuid;
    }
    return this.httpClient?.get(`${this.url}/new/template${path}`)
      .pipe(this.getDtoMapper());
  }

  findOpportunityVos(query?: any,
                     additionalParams?: any): Observable<JsonApiResource<OpportunitySummaryVO[], JsonApiListResourceMetadataBase>> {
    const queryString = new QueryParams(
      query,
      additionalParams ? additionalParams : []
    ).toString();
    return this.httpClient?.get<JsonApiResource<OpportunitySummaryVO[],JsonApiListResourceMetadataBase>>(`${this.url}` + '/vo/opportunitySummaryVos' + `${queryString}`)
      .pipe(
        map(
          (jsonApiResource) => {
            jsonApiResource.data = jsonApiResource.data.map(item =>
              AbstractResourceService.resourceMapper(OpportunitySummaryVO, item)
            );
            jsonApiResource.meta = AbstractResourceService.resourceMapper(
              JsonApiListResourceMetadataBase,
              jsonApiResource.meta
            );
            return jsonApiResource;
          }
        )
      );
  }

  getMetaDetail(uuid: string): Observable<OpportunityMetaDetailModel> {
    return <Observable<OpportunityMetaDetailModel>>this.httpClient?.get(`${this.url}/meta-details/${uuid}`);
  }

  findAllVersionForOpportunity(uuid: any, query?: any, additionalParams?: any): Observable<JsonApiResource<OpportunityVersionVoModel[], OpportunityListMeta>> {
  const queryString = new QueryParams(
    query,
    additionalParams ? additionalParams : []).toString();

  return this.httpClient?.get(`${this.url}/versions/${uuid}${queryString}`).pipe(this.mapToListOfObjects());
  }

  findAccountByOppUuid(id: string): Observable<JsonApiResource<any, any>> {
    return this.httpClient?.get(`${this.url}/account/${id}`).pipe(this.getDtoMapper());
  }
}
